import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import Breadcrumb from '../../components/Breadcrumb';
import { getAnalyticsReport } from '../../api/analytics';
import type { AnalyticsReport as AnalyticsReportType } from '../../types/analytics';
import { getStageInfo } from './analyticsStages';
import { parseSummaryCards } from './parseSummaryCards';
import './Analytics.css';

function renderMarkdown(md: string): string {
  return DOMPurify.sanitize(marked.parse(md, { async: false }) as string);
}

export default function AnalyticsReport() {
  const { t } = useTranslation('admin');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<AnalyticsReportType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    getAnalyticsReport(id)
      .then(setReport)
      .catch(() => navigate('/admin/analytics'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const sanitizedAnalysis = useMemo(() => {
    if (!report?.freeAnalysis) return '';
    return renderMarkdown(report.freeAnalysis);
  }, [report?.freeAnalysis]);

  if (loading) {
    return (
      <div className="container" style={{ maxWidth: 900, margin: '2rem auto' }}>
        <div className="empty-state">
          <div className="loading-spinner" style={{ width: 32, height: 32, borderWidth: 3 }} />
          <p>{t('loading', { ns: 'common' })}</p>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const summaryCards = parseSummaryCards(report.summaryCards);

  return (
    <div className="container analytics-report-page">
      <Breadcrumb items={[
        { label: t('analytics.breadcrumbHome'), to: '/projects' },
        { label: t('analytics.report.breadcrumbAnalytics'), to: '/admin/analytics' },
        { label: t('analytics.report.title') },
      ]} />

      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ marginBottom: '0.25rem' }}>{t('analytics.report.title')}</h1>
          <p style={{ color: 'var(--text-3)', fontSize: '0.82rem', margin: 0 }}>
            {new Date(report.createdAt).toLocaleString()} · {report.triggerType.startsWith('auto') ? t('analytics.report.autoTrigger') : t('analytics.report.manualTrigger')} · {report.modelName}
          </p>
          <p className="analytics-report-range">
            {report.timeRangeStart && report.timeRangeEnd
              ? t('analytics.report.range', {
                start: report.timeRangeStart.slice(0, 10),
                end: report.timeRangeEnd.slice(0, 10),
              })
              : t('analytics.rangeUnavailable')}
          </p>
        </div>
        <button className="btn btn-ghost" onClick={() => navigate('/admin/analytics')}>
          {t('analytics.report.backDashboard')}
        </button>
      </div>

      {report.status === 'partial' && (
        <div role="alert" className="analytics-inline-warning">
          {t('analytics.report.partialWarning', { warning: report.qualityWarning || t('analytics.report.unknownQualityWarning') })}
        </div>
      )}

      {(report.promptFallbackReason || report.generatorBuildSha || report.generatorInstanceId || report.windowPolicy) && (
        <div className="card analytics-report-provenance">
          {report.promptFallbackReason && (
            <span>{t('analytics.report.promptFallback', { reason: report.promptFallbackReason })}</span>
          )}
          {(report.generatorBuildSha || report.generatorInstanceId) && (
            <span>{t('analytics.report.generatorProvenance', {
              build: report.generatorBuildSha || '—',
              instance: report.generatorInstanceId || '—',
            })}</span>
          )}
          {report.windowPolicy && <span>{t('analytics.report.windowPolicy', { policy: report.windowPolicy })}</span>}
        </div>
      )}

      {summaryCards && (
        <section style={{ marginBottom: '2rem' }}>
          <h2 className="section-title" style={{ marginBottom: '1rem' }}>{t('analytics.report.structuredAnalysis')}</h2>

          <div className="analytics-report-summary-grid">
            <div className="card" style={{ padding: '1rem 1.25rem', background: 'var(--success-light)', borderColor: 'transparent' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--success)', fontWeight: 500, marginBottom: '0.25rem' }}>{t('analytics.report.overallFirstPass')}</div>
              <div style={{ fontSize: '1.75rem', fontWeight: 600, color: 'var(--success)' }}>
                {((summaryCards.overall_first_pass_rate ?? 0) * 100).toFixed(1)}%
              </div>
            </div>
            <div className="card" style={{ padding: '1rem 1.25rem', background: 'var(--warning-light)', borderColor: 'transparent' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--warning)', fontWeight: 500, marginBottom: '0.25rem' }}>
                {summaryCards.intervention_density !== undefined ? t('analytics.report.densityLabel') : t('analytics.report.overallIntervention')}
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: 600, color: 'var(--warning)' }}>
                {summaryCards.intervention_density !== undefined
                  ? `${summaryCards.intervention_density.toFixed(2)} ${t('analytics.densityUnit')}`
                  : `${((summaryCards.legacy_intervention_rate ?? 0) * 100).toFixed(1)}%`}
              </div>
            </div>
          </div>

          {summaryCards.key_findings && summaryCards.key_findings.length > 0 && (
            <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem 0', fontWeight: 600 }}>{t('analytics.report.keyFindings')}</h3>
              {summaryCards.key_findings.map((f, i) => (
                <div key={i} style={{ fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '0.25rem' }}>· {f}</div>
              ))}
            </div>
          )}

          {summaryCards.by_stage && summaryCards.by_stage.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {summaryCards.by_stage.map((stage) => {
                const info = getStageInfo(stage.stage);
                return (
                <div className="card" key={stage.stage} style={{ padding: '1rem 1.25rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', minWidth: 0 }}>
                      <strong style={{ fontSize: '0.9rem' }}>{info.name}</strong>
                      {info.step && (
                        <span style={{ color: 'var(--text-3)', fontSize: '0.75rem' }}>{info.step}</span>
                      )}
                    </div>
                    <span style={{ color: 'var(--text-3)', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {stage.has_rate === false || stage.intervention_rate === undefined
                        ? t('analytics.report.countOnly', { count: stage.event_count })
                        : t('analytics.report.interventionRateCount', { rate: ((stage.intervention_rate ?? 0) * 100).toFixed(1), count: stage.event_count })}
                    </span>
                  </div>
                  {stage.top_reasons && stage.top_reasons.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', marginBottom: stage.suggested_action ? '0.5rem' : 0 }}>
                      {stage.top_reasons.map((reason, j) => (
                        <div key={j} style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>
                          · {reason.reason}
                          {reason.evidence ? ` — ${reason.evidence}` : ''}
                          {reason.count !== undefined ? ` (${reason.count}${reason.pct !== undefined ? `, ${(reason.pct * 100).toFixed(0)}%` : ''})` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                  {stage.suggested_action && (
                    <div style={{ fontSize: '0.82rem', color: 'var(--primary)', marginTop: '0.25rem' }}>
                      {t('analytics.report.suggestion', { action: stage.suggested_action })}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {summaryCards.vs_previous && (
            <div className="card" style={{ padding: '1rem 1.25rem', marginTop: '1rem', background: 'var(--surface-2)' }}>
              <h3 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem 0', fontWeight: 600 }}>{t('analytics.report.vsPrevious')}</h3>
              {summaryCards.vs_previous.first_pass_rate_delta !== undefined && (
                <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', margin: 0 }}>
                  {t('analytics.report.firstPassDelta')}: {summaryCards.vs_previous.first_pass_rate_delta > 0 ? '+' : ''}
                  {(summaryCards.vs_previous.first_pass_rate_delta * 100).toFixed(1)}%
                </p>
              )}
              {summaryCards.vs_previous.intervention_density_delta !== undefined && (
                <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', margin: '0.25rem 0 0' }}>
                  {t('analytics.report.densityDelta')}: {summaryCards.vs_previous.intervention_density_delta > 0 ? '+' : ''}
                  {summaryCards.vs_previous.intervention_density_delta.toFixed(2)} {t('analytics.densityUnit')}
                </p>
              )}
              {summaryCards.vs_previous.intervention_rate_delta !== undefined && (
                <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', margin: '0.25rem 0 0' }}>
                  {t('analytics.report.interventionDelta')} {summaryCards.vs_previous.intervention_rate_delta > 0 ? '+' : ''}
                  {(summaryCards.vs_previous.intervention_rate_delta * 100).toFixed(1)}%
                </p>
              )}
              {summaryCards.vs_previous.config_change_impact && (
                <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', margin: '0.25rem 0 0' }}>
                  {summaryCards.vs_previous.config_change_impact}
                </p>
              )}
            </div>
          )}

          {summaryCards.cost && (
            <div className="card" style={{ padding: '1rem 1.25rem', marginTop: '1rem' }}>
              <h3 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem 0', fontWeight: 600 }}>{t('analytics.report.costTitle')}</h3>
              <div className="analytics-report-cost-grid">
                <span>LLM: {(summaryCards.cost.llm_total_tokens ?? 0).toLocaleString()} tokens / ¥{(summaryCards.cost.llm_cost ?? 0).toFixed(2)}</span>
                <span>{t('analytics.report.imageCost')}: {summaryCards.cost.image_count ?? 0} / ¥{(summaryCards.cost.image_cost ?? 0).toFixed(2)}</span>
                <span>{t('analytics.report.videoCost')}: {summaryCards.cost.video_seconds ?? 0}s / ¥{(summaryCards.cost.video_cost ?? 0).toFixed(2)}</span>
                <span>{t('analytics.report.serviceCost')}: {summaryCards.cost.service_count ?? 0} / ¥{(summaryCards.cost.service_cost ?? 0).toFixed(2)} / {summaryCards.cost.service_credits ?? 0}</span>
              </div>
              {summaryCards.cost.top_steps && summaryCards.cost.top_steps.length > 0 && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: 'var(--text-3)' }}>
                  {t('analytics.report.topSteps')}: {summaryCards.cost.top_steps.map((s) => `${s.step}(¥${(s.cost ?? 0).toFixed(2)})`).join(' · ')}
                </div>
              )}
            </div>
          )}

          {summaryCards.config_changes && summaryCards.config_changes.length > 0 && (
            <div className="card" style={{ padding: '1rem 1.25rem', marginTop: '1rem' }}>
              <h3 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem 0', fontWeight: 600 }}>{t('analytics.report.configChanges')}</h3>
              <p className="analytics-muted">{t('analytics.report.configChangeScope')}</p>
              {summaryCards.config_changes.map((ch, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: 'var(--text-2)', marginBottom: '0.4rem' }}>
                  <div>[{ch.time}] {ch.type} — {ch.summary}</div>
                  {(ch.before || ch.after) && (
                    <div style={{ color: 'var(--text-3)', fontSize: '0.78rem' }}>
                      {ch.before && `${t('analytics.report.beforeWindow')}: ${(ch.before.intervention_density ?? 0).toFixed(2)} ${t('analytics.densityUnit')} / ${(ch.before.first_pass_rate * 100).toFixed(0)}%`}
                      {ch.before && ch.before_window && (
                        <span style={{ color: 'var(--text-3)', fontSize: '0.72rem' }}> [{ch.before_window}]</span>
                      )}
                      {ch.before && ch.after && ' → '}
                      {ch.after && `${t('analytics.report.afterWindow')}: ${(ch.after.intervention_density ?? 0).toFixed(2)} ${t('analytics.densityUnit')} / ${(ch.after.first_pass_rate * 100).toFixed(0)}%`}
                      {ch.after && ch.after_window && (
                        <span style={{ color: 'var(--text-3)', fontSize: '0.72rem' }}> [{ch.after_window}]</span>
                      )}
                      {ch.after && ch.after_truncated && (
                        <span style={{ color: 'var(--text-3)', fontSize: '0.72rem' }}> {t('analytics.report.windowTruncated')}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {sanitizedAnalysis && (
        <section>
          <h2 className="section-title" style={{ marginBottom: '1rem' }}>{t('analytics.report.deepAnalysis')}</h2>
          <div className="card markdown-body"
            style={{ padding: '1.5rem', lineHeight: 1.8, fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: sanitizedAnalysis }}
          />
        </section>
      )}
    </div>
  );
}
